/**
 * PoolPresenceCard — « où est Thomas Roussel, et avec quel véhicule ? »,
 * « qui détient CD-113-31 ? » lu depuis la FICHE (JGS, 2026-09-14).
 *
 * Un chef d'U.T. ne passe pas par l'onglet « Maintenant » du gestionnaire pour
 * répondre à ça : il ouvre la fiche de l'agent ou celle du véhicule. Cet encart
 * y pose l'état COURANT, et rien d'autre.
 *
 * ⚠️ LA GARDE N'EST PAS LA DOTATION. La garde (pool) dure quelques heures ; la
 * dotation (`assigned_vehicle_id`) dure des mois. Les confondre, c'est envoyer
 * quelqu'un chercher un véhicule qui est dehors. L'encart montre la garde EN
 * GROS et rappelle la dotation en petit dessous — il ne remplace jamais
 * l'affichage existant de la dotation dans la fiche.
 *
 * ⚠️ RIEN NE S'ÉCRIT ICI : lecture seule, rafraîchie toute les minutes. Les
 * gestes sur une garde (clôture d'office, immobilisation) restent au
 * gestionnaire, dans `PoolCustodyPanel`.
 *
 * ⚠️ LE RETARD SE LIT EN DURÉE, jamais en horodatage brut — même règle que le
 * panneau gestionnaire, même helper (`formatDuration`).
 *
 * Hors périmètre du module pool (404) ou erreur : l'encart DISPARAÎT. Une fiche
 * d'un parc sans pool ne doit pas porter un bandeau rouge qui n'appelle aucune
 * action.
 *
 * Mise en page : l'identité (plaque ou agent) est sur sa propre ligne, en gras,
 * sous son étiquette. C'est ce qui permet de garder UNE phrase traduisible par
 * ligne — découper « Détient {{plaque}} depuis {{heure}} » en morceaux pour
 * poser un `<strong>` au milieu rend la phrase intraduisible.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
// `Map` de lucide est renommé : sous son nom, il masquerait la `Map` native que
// `usePoolCustodyIndex` construit — « Map is not a constructor », mesuré le 14/09.
import { Car, User, CalendarClock, MapPin, Map as MapIcon } from 'lucide-react';
import { poolPresenceAPI, poolCustodyAPI } from '../../api/pool.js';
import { useFormat } from '../../i18n/format.js';
import { formatDuration } from './PoolCustodyPanel.jsx';
import PoolCustodyMapModal from './PoolCustodyMapModal.jsx';

/** Une minute : la garde se compte en heures, un rafraîchissement plus serré ne dit rien de neuf. */
const REFRESH_MS = 60_000;

/** « CD-113-31 · Renault Kangoo » — le libellé seul quand la plaque manque, et l'inverse. */
export function vehicleIdentity(source) {
  if (!source) return null;
  const parts = [source.license_plate, source.vehicle_label].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Index des gardes EN COURS pour les LISTES — UNE lecture pour tout l'écran.
 *
 * ⚠️ Jamais un appel par ligne : à 900 véhicules, une liste qui interroge le
 * serveur par ligne met l'écran à genoux. `limit` explicite parce que le
 * serveur tronque à 200 sans le dire.
 *
 * Erreur silencieuse : une liste de véhicules doit rester lisible quand le
 * module pool n'est pas là.
 */
export function usePoolCustodyIndex() {
  const { data } = useQuery({
    queryKey: ['poolCustody', 'index'],
    queryFn: () => poolCustodyAPI.list({ limit: 1000 }),
    refetchInterval: REFRESH_MS,
    staleTime: 30_000,
    retry: false,
  });

  return useMemo(() => {
    const rows = data?.ok ? (data.data?.custodies || []) : [];
    // Les réservations À VENIR aussi (retour JGS 14/09 : réservé au guichet,
    // l'agent change d'état dans la liste sans attendre la prise). La garde
    // gagne sur la réservation : on ne remplace jamais « dehors » par « réservé ».
    const upcoming = data?.ok ? (data.data?.upcoming || []) : [];
    const byDriver = new Map();
    const byVehicle = new Map();
    for (const row of upcoming) {
      const tagged = { ...row, upcoming: true };
      if (row.driver_id != null && !byDriver.has(String(row.driver_id))) byDriver.set(String(row.driver_id), tagged);
      if (row.vehicle_id != null && !byVehicle.has(String(row.vehicle_id))) byVehicle.set(String(row.vehicle_id), tagged);
    }
    for (const row of rows) {
      if (row.driver_id != null) byDriver.set(String(row.driver_id), row);
      if (row.vehicle_id != null) byVehicle.set(String(row.vehicle_id), row);
    }
    return { byDriver, byVehicle };
  }, [data]);
}

/**
 * Le badge compact des listes : « En déplacement », rouge quand c'est en retard.
 * Le `title` porte l'autre bout de la garde — la plaque côté agent, l'agent
 * côté véhicule.
 */
export function PoolPresenceBadge({ custody, kind }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  if (!custody) return null;

  // Réservé, pas encore pris : gris, avec l'heure de départ — un état, pas une alerte.
  if (custody.upcoming) {
    const when = custody.planned_start ? new Date(custody.planned_start) : null;
    const time = when && !Number.isNaN(when.getTime()) ? fmt.time(when) : '—';
    const upcomingTitle = kind === 'driver'
      ? t('poolPresence.badge.reservedWithVehicle', { vehicle: vehicleIdentity(custody) || '—', time })
      : t('poolPresence.badge.reservedWithDriver', { driver: custody.driver_label || '—', time });
    return (
      <span
        title={upcomingTitle}
        className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
      >
        {t('poolPresence.badge.reserved', { time })}
      </span>
    );
  }

  const title = kind === 'driver'
    ? t('poolPresence.badge.withVehicle', { vehicle: vehicleIdentity(custody) || '—' })
    : t('poolPresence.badge.withDriver', { driver: custody.driver_label || '—' });

  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        custody.late ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
      }`}
    >
      {custody.late ? t('poolPresence.badge.late') : t('poolPresence.badge.onTheRoad')}
    </span>
  );
}

/** Une ligne « étiquette / identité / quand », commune à la garde et à la réservation. */
function PresenceLine({ icon: Icon, tone, label, identity, detail, destination, right }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3">
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <p className="truncate font-semibold text-gray-900">{identity}</p>
        {detail && <p className="text-sm text-gray-600">{detail}</p>}
        {destination && (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{t('poolPresence.destination', { destination })}</span>
          </p>
        )}
      </div>
      {right}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {'driver'|'vehicle'} props.kind - de quelle fiche on parle
 * @param {number|string} props.id - l'identifiant de l'agent ou du véhicule
 */
/**
 * @param {'driver'|'vehicle'} kind
 * @param {number|string} id
 * @param {string} [subjectLabel] - le nom du sujet de la fiche (l'agent, sur une fiche
 *   conducteur) : la présence d'un conducteur ne se nomme pas elle-même, et la carte
 *   afficherait « Agent non précisé » (mesuré le 14/09).
 */
export function PoolPresenceCard({ kind, id, subjectLabel = null }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const isDriver = kind === 'driver';
  const [mapOpen, setMapOpen] = useState(false);

  const { data, isError, isLoading } = useQuery({
    queryKey: ['poolPresence', kind, String(id ?? '')],
    queryFn: () => (isDriver ? poolPresenceAPI.forDriver(id) : poolPresenceAPI.forVehicle(id)),
    enabled: id != null && id !== '',
    refetchInterval: REFRESH_MS,
    retry: false,
  });

  const presence = data?.ok ? data.data : null;

  // Hors périmètre (404) ou panne : la fiche se passe de l'encart.
  if (id == null || id === '' || isError) return null;

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
        <div className="mt-3 h-4 w-48 animate-pulse rounded bg-gray-100" />
      </div>
    );
  }

  if (!presence) return null;

  const now = presence.now || undefined;

  // Même jour que « maintenant » : l'heure suffit. Sinon la date, sans quoi
  // « 17:45 » d'après-demain se lit comme « tout à l'heure ».
  const stamp = (value) => {
    if (!value) return null;
    const when = new Date(value);
    if (Number.isNaN(when.getTime())) return null;
    const ref = now ? new Date(now) : new Date();
    const sameDay = !Number.isNaN(ref.getTime())
      && when.getFullYear() === ref.getFullYear()
      && when.getMonth() === ref.getMonth()
      && when.getDate() === ref.getDate();
    return sameDay ? fmt.time(when) : fmt.dateTime(when);
  };

  const custody = presence.custody || null;
  const next = !custody ? (presence.next_reservation || null) : null;
  const assigned = isDriver ? presence.assigned_vehicle : presence.assigned_driver;

  let body = null;

  if (custody) {
    const due = stamp(custody.due_at);
    const identity = isDriver
      ? (vehicleIdentity(custody) || t('poolPresence.driver.unknownVehicle'))
      : (custody.driver_label || t('poolPresence.vehicle.unknownDriver'));
    const since = stamp(custody.taken_at);
    const detail = due
      ? t('poolPresence.since', { since: since || '—', due })
      : t('poolPresence.sinceNoDue', { since: since || '—' });

    const right = custody.late
      ? (
        <span className="shrink-0 rounded-full bg-red-50 px-2.5 py-1 text-sm font-semibold text-red-700">
          {t('poolPresence.late', { duration: formatDuration(custody.overdue_min) })}
        </span>
      )
      : custody.remaining_min != null
        ? (
          <span className="shrink-0 text-sm text-gray-500">
            {t('poolPresence.remaining', { duration: formatDuration(custody.remaining_min) })}
          </span>
        )
        : null;

    body = (
      <PresenceLine
        icon={isDriver ? Car : User}
        tone={custody.late ? 'text-red-500' : 'text-blue-600'}
        label={isDriver ? t('poolPresence.driver.holdsLabel') : t('poolPresence.vehicle.heldByLabel')}
        identity={identity}
        detail={detail}
        destination={custody.destination_label}
        right={right}
      />
    );
  } else if (next) {
    const identity = isDriver
      ? (vehicleIdentity(next)
        || (next.vehicle_class
          ? t('poolPresence.driver.byClass', { vehicleClass: next.vehicle_class })
          : t('poolPresence.driver.unknownVehicle')))
      : (next.driver_label || t('poolPresence.vehicle.unknownDriver'));

    body = (
      <PresenceLine
        icon={CalendarClock}
        tone="text-gray-400"
        label={isDriver ? t('poolPresence.driver.reservedLabel') : t('poolPresence.vehicle.reservedLabel')}
        identity={identity}
        detail={t('poolPresence.window', {
          from: stamp(next.planned_start) || '—',
          to: stamp(next.planned_end) || '—',
        })}
        destination={next.destination_label}
      />
    );
  } else {
    body = (
      <p className="text-sm text-gray-500">
        {isDriver ? t('poolPresence.driver.none') : t('poolPresence.vehicle.none')}
      </p>
    );
  }

  const assignedLabel = assigned
    ? (isDriver
      ? t('poolPresence.assignedVehicle', { vehicle: vehicleIdentity(assigned) || '—' })
      : t('poolPresence.assignedDriver', { driver: assigned.driver_label || '—' }))
    : null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t('poolPresence.title')}
      </p>
      {body}
      {(custody || next?.route) && (
        <button
          type="button"
          onClick={() => setMapOpen(true)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
        >
          <MapIcon className="h-4 w-4" />
          {t('poolPresence.mapButton')}
        </button>
      )}
      {assignedLabel && (
        <p className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500">{assignedLabel}</p>
      )}
      {mapOpen && (custody || next) && (
        <PoolCustodyMapModal
          custody={(() => {
            const subject = custody || next;
            return kind === 'driver' && subjectLabel && !subject.driver_label ? { ...subject, driver_label: subjectLabel } : subject;
          })()}
          upcoming={!custody}
          kind={kind}
          onClose={() => setMapOpen(false)}
        />
      )}
    </div>
  );
}

export default PoolPresenceCard;
